import React, { useEffect, useRef } from 'react';
import { gsap } from '../../animations/gsap.config';
import { ScrollTrigger } from '../../animations/gsap.config';
import careersData from '../../data/careers.json';
import { 
  Send, 
  Eye, 
  MessageCircle, 
  CheckCircle, 
  Mail, 
  Phone,
  ArrowRight,
  FileText
} from 'lucide-react';

const ProcessStep = ({ step, index, isLast }) => {
  const stepRef = useRef(null);
  const iconMap = {
    1: Send,
    2: Eye,
    3: MessageCircle,
    4: CheckCircle,
  };

  const IconComponent = iconMap[step.step];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        stepRef.current,
        { y: 60, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          delay: index * 0.2,
          ease: 'back.out(1.2)',
          scrollTrigger: {
            trigger: stepRef.current,
            start: 'top 85%',
          },
        }
      );
    }, stepRef);

    return () => ctx.revert();
  }, [index]);

  return (
    <div ref={stepRef} className="relative">
      {/* Step Card */}
      <div className="group relative bg-white rounded-3xl p-8 border border-slate-200 hover:border-primary-300 transition-all duration-500 hover:shadow-xl hover:-translate-y-2">
        {/* Background Glow */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-50/30 to-secondary-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Content */}
        <div className="relative z-10 text-center">
          {/* Step Number & Icon */}
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-2xl bg-primary-100 border-2 border-primary-200 flex items-center justify-center mx-auto group-hover:bg-primary-200 group-hover:scale-110 transition-all duration-500">
              <IconComponent size={32} className="text-primary-600" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-secondary-500 text-white text-sm font-bold flex items-center justify-center">
              {step.step}
            </div>
          </div>

          {/* Title */}
          <h3 className="font-display font-bold text-slate-900 text-xl mb-4 group-hover:text-primary-700 transition-colors duration-300">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
            {step.description}
          </p>
        </div>
      </div>

      {/* Connector Arrow */}
      {!isLast && (
        <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
          <div className="w-8 h-8 rounded-full bg-white border-2 border-primary-200 flex items-center justify-center shadow-lg">
            <ArrowRight size={16} className="text-primary-600" />
          </div>
        </div>
      )}
    </div>
  );
};

const ApplicationProcess = () => {
  const { application } = careersData;
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        headerRef.current?.children ?? [],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.2,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
          },
        }
      );

      // Contact section animation
      gsap.fromTo(
        contactRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: contactRef.current,
            start: 'top 85%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleEmailClick = () => {
    const subject = 'Career Inquiry - Agora Consulting';
    const body = `Dear Agora Consulting Team,

I am interested in learning more about career opportunities at your organization.

Please let me know about current and upcoming positions that might be a good fit for my background.

Best regards,
[Your Name]`;
    
    window.location.href = `mailto:${application.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 bg-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-primary-600/5 blur-[120px] rounded-full" />
      </div>

      <div className="container mx-auto relative z-10 px-6 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tighter leading-none">
            {application.headline} <span className="bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">{application.headlineAccent}</span>
          </h2>
          <p className="text-slate-600 text-lg md:text-xl font-light leading-relaxed">
            {application.description}
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 mb-20">
          {application.process.map((step, index) => (
            <ProcessStep 
              key={step.step} 
              step={step} 
              index={index} 
              isLast={index === application.process.length - 1}
            />
          ))}
        </div>

        {/* Contact Information */}
        <div ref={contactRef} className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 md:p-12 border border-slate-200">
            <div className="text-center mb-8">
              <h3 className="font-display font-bold text-slate-900 text-2xl md:text-3xl mb-4">
                Ready to Start Your Journey?
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {application.contact.note}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Email Contact */}
              <div className="group">
                <button
                  onClick={handleEmailClick}
                  className="w-full p-6 bg-white rounded-2xl border border-slate-200 hover:border-primary-300 transition-all duration-500 hover:shadow-lg hover:-translate-y-1 text-left"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 border border-primary-200 flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-300">
                      <Mail size={20} className="text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 group-hover:text-primary-700 transition-colors duration-300">
                        Email Application
                      </h4>
                      <p className="text-sm text-slate-600">Send us your CV and cover letter</p>
                    </div>
                  </div>
                  <p className="text-sm text-primary-600 font-medium break-all">
                    {application.contact.email}
                  </p>
                </button>
              </div>

              {/* Phone Contact */}
              <div className="group">
                <a
                  href={`tel:${application.contact.phone}`}
                  className="block w-full p-6 bg-white rounded-2xl border border-slate-200 hover:border-secondary-300 transition-all duration-500 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary-100 border border-secondary-200 flex items-center justify-center group-hover:bg-secondary-200 transition-colors duration-300">
                      <Phone size={20} className="text-secondary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 group-hover:text-secondary-700 transition-colors duration-300">
                        Call Us
                      </h4>
                      <p className="text-sm text-slate-600">Speak directly with our HR team</p>
                    </div>
                  </div>
                  <p className="text-sm text-secondary-600 font-medium">
                    {application.contact.phone}
                  </p>
                </a>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 p-6 bg-white/50 rounded-2xl border border-slate-200">
              <div className="flex items-start gap-3">
                <FileText size={20} className="text-primary-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Application Requirements</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Updated CV/Resume in PDF format</li>
                    <li>• Cover letter explaining your interest and qualifications</li>
                    <li>• Portfolio or work samples (if applicable)</li>
                    <li>• Contact information for 2-3 professional references</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationProcess;